import Carregando from '../Carregando';
import { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, Alert, Chip, IconButton, InputAdornment, Tooltip, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SpeedIcon from '@mui/icons-material/Speed';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import dayjs from 'dayjs';
import api from '../../services/api';
import { formatDias } from '../../utils/formatters';
import { validarArquivoContrato, EXTENSOES_CONTRATO } from '../contratoUtils';
import { formatarReal } from '../financeiro/financeiroUtils';
import { abrirArquivoContrato, situacaoConfig, tamanhoLegivel, textoDoErro } from './contratosNovosUtils';

function SecaoForm({ titulo, icone, cor, children }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.5, pb: 0.8, borderBottom: `2px solid ${cor}` }}>
        {icone}
        <Typography sx={{ fontWeight: 700, fontSize: 12.5, textTransform: 'uppercase', letterSpacing: '0.05em', color: cor }}>
          {titulo}
        </Typography>
      </Box>
      <Grid container spacing={2}>
        {children}
      </Grid>
    </Box>
  );
}

const STATUS_PROJETO = {
  planejamento: { label: 'Planejamento', color: 'default' },
  em_andamento: { label: 'Em andamento', color: 'primary' },
  pausado: { label: 'Pausado', color: 'warning' },
  concluido: { label: 'Concluído', color: 'success' },
  cancelado: { label: 'Cancelado', color: 'error' },
};

const FORM_VAZIO = {
  nome: '',
  cliente: '',
  valor_contrato: '',
  carga_total_contratada: '',
  workshops_vendidos: '',
};

const CLIENTE_NOVO_VAZIO = {
  nome: '', codigo_agenda: '', segmento: '', contato_nome: '', contato_email: '', contato_telefone: '',
};

// Aba "Contratos" da tela Comercial: o Comercial cadastra o contrato (SEM projeto). Quem inicia
// o projeto adiciona os projetos, a célula e os consultores na tela Projetos.
// Com `projetosAntigos`, a lista mostra também as vendas feitas antes dos contratos novos (1 projeto
// cada), para o Comercial ver tudo num lugar só.
export default function ContratosComercial({
  clientes, recarregarClientes, projetosAntigos = null, onEditarAntigo, onAbrirContratoAntigo,
}) {
  const [contratos, setContratos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');

  const [abrirModal, setAbrirModal] = useState(false);
  const [editando, setEditando] = useState(null);          // null = cadastro novo
  const [form, setForm] = useState(FORM_VAZIO);
  const [clienteNovo, setClienteNovo] = useState(false);
  const [formClienteNovo, setFormClienteNovo] = useState(CLIENTE_NOVO_VAZIO);
  const [arquivo, setArquivo] = useState(null);
  const [erroArquivo, setErroArquivo] = useState('');
  const [motivoValor, setMotivoValor] = useState('');
  const [salvando, setSalvando] = useState(false);
  const modoEdicao = Boolean(editando);
  const temProjetos = modoEdicao && (editando.quantidade_projetos || 0) > 0;

  const carregar = () => api.get('/contratos/')
    .then((res) => { setContratos(res.data); setErro(''); })
    .catch((err) => setErro(textoDoErro(err, 'Não foi possível carregar os contratos.')))
    .finally(() => setLoading(false));

  useEffect(() => { carregar(); }, []);

  const limparAnexo = () => { setArquivo(null); setErroArquivo(''); };

  const abrirNovo = () => {
    setEditando(null);
    setForm(FORM_VAZIO);
    setClienteNovo(false);
    setFormClienteNovo(CLIENTE_NOVO_VAZIO);
    setMotivoValor('');
    limparAnexo();
    setErro('');
    setAbrirModal(true);
  };

  const abrirEdicao = (c) => {
    setEditando(c);
    setForm({
      nome: c.nome || '',
      cliente: c.cliente || '',
      valor_contrato: c.valor_contrato ?? '',
      carga_total_contratada: c.carga_total_contratada ?? '',
      workshops_vendidos: c.workshops_vendidos ?? 0,
    });
    setClienteNovo(false);
    setFormClienteNovo(CLIENTE_NOVO_VAZIO);
    setMotivoValor('');
    limparAnexo();
    setErro('');
    setAbrirModal(true);
  };

  const escolherArquivo = (e) => {
    const escolhido = e.target.files?.[0] || null;
    e.target.value = '';           // permite escolher o mesmo arquivo de novo
    if (!escolhido) return;
    const problema = validarArquivoContrato(escolhido);
    setErroArquivo(problema || '');
    setArquivo(problema ? null : escolhido);
  };

  const valorAlterado = modoEdicao && Number(form.valor_contrato) !== Number(editando.valor_contrato ?? 0);

  const validar = () => {
    if (!clienteNovo && !form.cliente) return 'Selecione o cliente, ou cadastre um novo.';
    if (clienteNovo && !formClienteNovo.nome.trim()) return 'O nome do cliente novo é obrigatório.';
    if (!(Number(form.valor_contrato) > 0)) return 'Informe o valor total do contrato.';
    if (!modoEdicao && !arquivo) return 'Anexe o contrato assinado para cadastrar o contrato.';
    const q = form.workshops_vendidos;
    if (q !== '' && (!Number.isInteger(Number(q)) || Number(q) < 0)) return 'A quantidade de workshops deve ser um número inteiro (0 ou mais).';
    if (form.carga_total_contratada !== '' && Number(form.carga_total_contratada) < 0) return 'A carga contratada não pode ser negativa.';
    return '';
  };

  const salvarNovo = async (clienteId) => {
    const dados = new FormData();
    dados.append('nome', form.nome.trim());
    dados.append('cliente', clienteId);
    dados.append('valor_contrato', String(form.valor_contrato));
    dados.append('carga_total_contratada', form.carga_total_contratada || 0);
    dados.append('workshops_vendidos', form.workshops_vendidos || 0);
    dados.append('contrato_arquivo', arquivo);
    await api.post('/contratos/', dados);
  };

  const salvarEdicao = async (clienteId) => {
    const payload = {
      nome: form.nome.trim() || editando.nome,
      cliente: clienteId,
      carga_total_contratada: form.carga_total_contratada === '' ? 0 : form.carga_total_contratada,
      workshops_vendidos: form.workshops_vendidos === '' ? 0 : parseInt(form.workshops_vendidos, 10),
    };
    if (valorAlterado) {
      payload.valor_contrato = String(form.valor_contrato);
      payload.motivo_valor_contrato = motivoValor.trim();
    }
    await api.patch(`/contratos/${editando.id}/`, payload);
    if (arquivo) {
      const dados = new FormData();
      dados.append('contrato_arquivo', arquivo);
      try {
        await api.patch(`/contratos/${editando.id}/`, dados);
      } catch (err) {
        const e = new Error('arquivo');
        e.mensagemFinal = `Os dados foram salvos, mas o novo contrato não foi enviado: ${textoDoErro(err, 'erro no envio do arquivo.')}`;
        throw e;
      }
    }
  };

  const handleSalvar = async () => {
    setErro('');
    const problema = validar();
    if (problema) { setErro(problema); return; }
    setSalvando(true);
    try {
      let clienteId = form.cliente;
      if (clienteNovo) {
        const payloadCliente = { ...formClienteNovo };
        Object.keys(payloadCliente).forEach((k) => { if (!payloadCliente[k]) delete payloadCliente[k]; });
        const res = await api.post('/clientes/', payloadCliente);
        clienteId = res.data.id;
        if (recarregarClientes) recarregarClientes();
      }
      if (modoEdicao) await salvarEdicao(clienteId); else await salvarNovo(clienteId);
      setSucesso(modoEdicao ? 'Contrato atualizado com sucesso!' : 'Contrato cadastrado! Agora quem inicia o projeto pode adicionar os projetos dele na tela Projetos.');
      setAbrirModal(false);
      carregar();
    } catch (err) {
      setErro(err?.mensagemFinal || textoDoErro(err, modoEdicao ? 'Erro ao salvar o contrato.' : 'Erro ao cadastrar o contrato.'));
    } finally {
      setSalvando(false);
    }
  };

  const handleAbrirArquivo = async (c) => {
    try {
      await abrirArquivoContrato(c);
    } catch (err) {
      setErro(err.message);
    }
  };

  if (loading) return <Carregando />;

  // Contratos novos + vendas antigas, da mais recente para a mais antiga.
  const linhas = [
    ...contratos,
    ...(projetosAntigos || []).map((p) => ({ antigo: true, projeto: p, criado_em: p.criado_em })),
  ].sort((a, b) => String(b.criado_em || '').localeCompare(String(a.criado_em || '')));

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNovo} sx={{ backgroundColor: '#13375F' }}>
          Novo Contrato
        </Button>
      </Box>

      {erro && !abrirModal && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro('')}>{erro}</Alert>}
      {sucesso && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSucesso('')}>{sucesso}</Alert>}

      {linhas.length === 0 ? (
        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary" mb={1}>Nenhum contrato cadastrado ainda</Typography>
            <Typography variant="body2" color="text.secondary">
              Clique em "Novo Contrato" para registrar a primeira venda.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Card} sx={{ borderRadius: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Contrato</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Cliente</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Valor do contrato</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Carga contratada</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Workshops vendidos</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Situação</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Célula</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Contrato</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Cadastrado em</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Editar</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {linhas.map((c) => {
                if (c.antigo) {
                  const p = c.projeto;
                  const stp = STATUS_PROJETO[p.status] || STATUS_PROJETO.planejamento;
                  return (
                    <TableRow key={`p${p.id}`}>
                      <TableCell>
                        {p.nome}
                        <Typography component="span" sx={{ display: 'block', fontSize: 12, color: 'text.secondary' }}>Venda de 1 projeto</Typography>
                      </TableCell>
                      <TableCell>{p.cliente_nome}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                        {p.valor_contrato !== null && p.valor_contrato !== undefined && p.valor_contrato !== ''
                          ? formatarReal(Number(p.valor_contrato))
                          : <Chip label="Não informado" size="small" color="warning" variant="outlined" />}
                      </TableCell>
                      <TableCell align="right">{formatDias(p.carga_total_contratada)}</TableCell>
                      <TableCell align="right">{p.workshops_vendidos ?? 0}</TableCell>
                      <TableCell><Chip label={`1 projeto, ${stp.label.toLowerCase()}`} color={stp.color} size="small" /></TableCell>
                      <TableCell>{p.celula_nome || '—'}</TableCell>
                      <TableCell>
                        {p.tem_contrato ? (
                          <Tooltip title={`Abrir contrato${p.contrato_nome ? `: ${p.contrato_nome}` : ''}`}>
                            <IconButton size="small" aria-label={`Abrir contrato de ${p.nome}`} onClick={() => onAbrirContratoAntigo && onAbrirContratoAntigo(p)}>
                              <DescriptionOutlinedIcon fontSize="small" sx={{ color: '#13375F' }} />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Chip label="Sem contrato" size="small" color="warning" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>{p.criado_em ? dayjs(p.criado_em).format('DD/MM/YYYY') : '—'}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar venda">
                          <IconButton size="small" aria-label={`Editar ${p.nome}`} onClick={() => onEditarAntigo && onEditarAntigo(p)}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                }
                const st = situacaoConfig[c.situacao] || situacaoConfig.aguardando_projeto;
                const rotuloSituacao = c.situacao === 'com_projetos'
                  ? `${c.quantidade_projetos} projeto${c.quantidade_projetos > 1 ? 's' : ''}`
                  : st.label;
                return (
                  <TableRow key={c.id}>
                    <TableCell>{c.nome}</TableCell>
                    <TableCell>{c.cliente_nome}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                      {formatarReal(Number(c.valor_contrato))}
                    </TableCell>
                    <TableCell align="right">{formatDias(c.carga_total_contratada)}</TableCell>
                    <TableCell align="right">{c.workshops_vendidos ?? 0}</TableCell>
                    <TableCell><Chip label={rotuloSituacao} color={st.color} size="small" /></TableCell>
                    <TableCell>{c.celula_nome || '—'}</TableCell>
                    <TableCell>
                      {c.tem_contrato ? (
                        <Tooltip title={`Abrir contrato${c.contrato_nome ? `: ${c.contrato_nome}` : ''}`}>
                          <IconButton size="small" aria-label={`Abrir contrato de ${c.nome}`} onClick={() => handleAbrirArquivo(c)}>
                            <DescriptionOutlinedIcon fontSize="small" sx={{ color: '#13375F' }} />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Chip label="Sem contrato" size="small" color="warning" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>{c.criado_em ? dayjs(c.criado_em).format('DD/MM/YYYY') : '—'}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Editar contrato">
                        <IconButton size="small" aria-label={`Editar ${c.nome}`} onClick={() => abrirEdicao(c)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={abrirModal} onClose={salvando ? undefined : () => setAbrirModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>{modoEdicao ? 'Editar Contrato' : 'Novo Contrato'}</DialogTitle>
        <Typography variant="body2" color="text.secondary" sx={{ px: 3, mb: 1 }}>
          {modoEdicao ? 'Altere o que for preciso e salve' : 'Preencha os dados da venda. Os projetos são adicionados depois, por quem inicia o projeto.'}
        </Typography>
        <DialogContent>
          {erro && abrirModal && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

          <SecaoForm titulo="Cliente" icone={<BusinessIcon sx={{ fontSize: 17, color: '#2563EB' }} />} cor="#2563EB">
            {!clienteNovo ? (
              <>
                <Grid size={12}>
                  <Autocomplete
                    sx={{ width: '100%' }}
                    options={clientes}
                    disabled={temProjetos}
                    getOptionLabel={(c) => c.nome}
                    isOptionEqualToValue={(opcao, valor) => opcao.id === valor.id}
                    value={clientes.find((c) => c.id === form.cliente) || null}
                    onChange={(e, novoValor) => setForm({ ...form, cliente: novoValor?.id || '' })}
                    renderInput={(params) => (
                      <TextField
                        {...params} fullWidth required label="Cliente" placeholder="Buscar cliente..."
                        helperText={temProjetos ? 'Este contrato já tem projetos, então o cliente não pode ser trocado.' : undefined}
                      />
                    )}
                  />
                </Grid>
                {!temProjetos && (
                  <Grid size={12}>
                    <Button size="small" onClick={() => setClienteNovo(true)} sx={{ color: '#2563EB' }}>
                      + Cliente novo? Cadastre aqui
                    </Button>
                  </Grid>
                )}
              </>
            ) : (
              <>
                <Grid size={12}>
                  <TextField fullWidth required label="Nome do cliente" value={formClienteNovo.nome}
                    onChange={(e) => setFormClienteNovo({ ...formClienteNovo, nome: e.target.value })} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="Código na Agenda" value={formClienteNovo.codigo_agenda} helperText="Ex: RCH, BET, SKA"
                    onChange={(e) => setFormClienteNovo({ ...formClienteNovo, codigo_agenda: e.target.value })} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="Segmento" value={formClienteNovo.segmento}
                    onChange={(e) => setFormClienteNovo({ ...formClienteNovo, segmento: e.target.value })} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField fullWidth label="Contato — nome" value={formClienteNovo.contato_nome}
                    onChange={(e) => setFormClienteNovo({ ...formClienteNovo, contato_nome: e.target.value })} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField fullWidth label="Contato — e-mail" type="email" value={formClienteNovo.contato_email}
                    onChange={(e) => setFormClienteNovo({ ...formClienteNovo, contato_email: e.target.value })} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField fullWidth label="Contato — telefone" value={formClienteNovo.contato_telefone}
                    onChange={(e) => setFormClienteNovo({ ...formClienteNovo, contato_telefone: e.target.value })} />
                </Grid>
                <Grid size={12}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    CNPJ, endereço, célula e outros dados podem ser completados depois na tela de Clientes.
                  </Typography>
                  <Button size="small" onClick={() => setClienteNovo(false)} sx={{ color: '#2563EB' }}>
                    Usar um cliente já cadastrado
                  </Button>
                </Grid>
              </>
            )}
          </SecaoForm>

          <SecaoForm titulo="Informações do contrato" icone={<InfoOutlinedIcon sx={{ fontSize: 17, color: '#13375F' }} />} cor="#13375F">
            <Grid size={12}>
              <TextField
                fullWidth label="Nome do contrato" value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                helperText='Ex.: "Contrato Cliente X 2026". Se deixar em branco, usamos "Contrato" + o nome do cliente.'
              />
            </Grid>
            {modoEdicao && (
              <Grid size={12}>
                {editando.projetos?.length > 0 ? (
                  <Box>
                    <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#64748b', fontWeight: 600 }}>
                      Projetos deste contrato{editando.celula_nome ? ` (célula ${editando.celula_nome})` : ''}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {editando.projetos.map((p) => <Chip key={p.id} label={p.nome} size="small" variant="outlined" />)}
                    </Box>
                  </Box>
                ) : (
                  <Alert severity="info" sx={{ py: 0 }}>Este contrato ainda não tem projetos. Quem inicia o projeto adiciona na tela Projetos.</Alert>
                )}
              </Grid>
            )}
          </SecaoForm>

          <SecaoForm titulo="Valor do contrato" icone={<PaidOutlinedIcon sx={{ fontSize: 17, color: '#13375F' }} />} cor="#13375F">
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth required label="Valor total do contrato" type="number"
                value={form.valor_contrato}
                onChange={(e) => setForm({ ...form, valor_contrato: e.target.value })}
                slotProps={{ input: { startAdornment: <InputAdornment position="start">R$</InputAdornment> }, htmlInput: { min: 0, step: '0.01' } }}
                helperText={modoEdicao ? `Valor atual: ${formatarReal(Number(editando.valor_contrato))}. Se houve aditivo, informe o novo total.` : 'Total vendido. Segue com o contrato para o Financeiro'}
              />
            </Grid>
            {valorAlterado && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth label="Motivo da alteração (opcional)"
                  value={motivoValor} onChange={(e) => setMotivoValor(e.target.value)}
                  placeholder="Ex.: Aditivo 01"
                  slotProps={{ htmlInput: { maxLength: 300 } }}
                  helperText="Fica no histórico do valor"
                />
              </Grid>
            )}
          </SecaoForm>

          <SecaoForm titulo="Contrato assinado" icone={<AttachFileIcon sx={{ fontSize: 17, color: '#7C3AED' }} />} cor="#7C3AED">
            <Grid size={12}>
              {modoEdicao && (
                <Box sx={{ mb: 1.5 }}>
                  {editando.tem_contrato ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <DescriptionOutlinedIcon fontSize="small" sx={{ color: '#13375F' }} />
                      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{editando.contrato_nome || 'Contrato anexado'}</Typography>
                      <Button size="small" onClick={() => handleAbrirArquivo(editando)} sx={{ textTransform: 'none' }}>Abrir</Button>
                    </Box>
                  ) : (
                    <Alert severity="warning" sx={{ py: 0 }}>Este contrato ainda não tem arquivo anexado. Anexe abaixo.</Alert>
                  )}
                </Box>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  component="label" variant={arquivo ? 'outlined' : 'contained'} size="small"
                  startIcon={<AttachFileIcon />}
                  sx={arquivo ? { textTransform: 'none' } : { textTransform: 'none', backgroundColor: '#7C3AED', '&:hover': { backgroundColor: '#6D28D9' } }}
                >
                  {arquivo ? 'Trocar arquivo' : (modoEdicao ? 'Anexar novo contrato' : 'Anexar contrato')}
                  <input type="file" hidden accept={EXTENSOES_CONTRATO.join(',')} onChange={escolherArquivo} aria-label="Arquivo do contrato" />
                </Button>
                {arquivo && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{arquivo.name}</Typography>
                    <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>({tamanhoLegivel(arquivo.size)})</Typography>
                    <IconButton size="small" aria-label="Remover o arquivo escolhido" onClick={limparAnexo}>
                      <CloseOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: erroArquivo ? 'error.main' : 'text.secondary' }}>
                {erroArquivo || (modoEdicao
                  ? 'Opcional na edição: só anexe se quiser trocar o contrato atual. PDF, Word ou imagem, até 20 MB.'
                  : 'Obrigatório. PDF, Word ou imagem, até 20 MB. Sem o contrato não dá para cadastrar.')}
              </Typography>
            </Grid>
          </SecaoForm>

          <SecaoForm titulo="Carga contratada" icone={<SpeedIcon sx={{ fontSize: 17, color: '#16A34A' }} />} cor="#16A34A">
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth label="Carga total contratada (dias)" type="number"
                value={form.carga_total_contratada}
                onChange={(e) => setForm({ ...form, carga_total_contratada: e.target.value })}
                helperText="Uma carga só para o contrato inteiro: todos os projetos dele consomem dela"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth label="Quantidade de workshops vendidos" type="number"
                value={form.workshops_vendidos}
                onChange={(e) => setForm({ ...form, workshops_vendidos: e.target.value })}
                helperText="Também são do contrato: qualquer projeto dele pode usar"
              />
            </Grid>
          </SecaoForm>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAbrirModal(false)} disabled={salvando}>Cancelar</Button>
          <Button
            variant="contained" onClick={handleSalvar}
            disabled={salvando || (!modoEdicao && !arquivo)}
            sx={{ backgroundColor: '#13375F' }}
          >
            {salvando ? 'Salvando...' : (modoEdicao ? 'Salvar alterações' : 'Cadastrar')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
